import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import "./SettingsPage.css";
import { AppShell, SideGroup, SideLink } from "../app/AppShell";
import { useMe } from "../app/AuthGate";
import { useRouteParam } from "../lib/route";
import { UsersSection } from "./UsersSection";
import { AuthPolicySection } from "./AuthPolicySection";
import { RunnersSection } from "./RunnersSection";
import { McpTokensSection } from "./McpTokensSection";
import { Button } from "../components/Button";
import { Alert, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Select } from "../components/Field";
import { Chip } from "../components/Status";
import { TRACKS } from "../chat/api";
import { hole, rich, tx } from "../i18n";

/* ══ 배차 벤더 — 웹/DB 층 ══
   채택 시안(안 A · 표 하나, Figma node-id=50-3). `docs/decisions/naru-dispatch.md`
   「단계마다 에이전트를 고른다」가 세 층을 정했다 — 웹/DB(이 화면) > env(.env 기본값)
   > 안 고름(맡을 수 있는 첫 벤더). 이 화면은 그 중 첫 층만 만진다.

   **여기서 지운 것과 애초에 안 고른 것은 같은 값이다** — 둘 다 「env 기본값이
   채운다」(`GET/PUT /dispatch/vendors`, `crates/api/src/dispatch_vendor.rs`). 그래서
   화면에는 둘째 뜻(예: b3x-pr 의 빈 목록)이 없다. */

const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

/** 머지는 세션을 안 열어 벤더가 없다 — 표에서 뺀다(`Track::opens_session`). */
const PICKABLE = TRACKS.filter((t) => t.key !== "merge");

type Picks = Record<string, string | null>;
type Saved = { tracks: Picks; knownVendors: string[] };

/** `<select>` 는 빈 문자열만 값으로 갖는다 — `null` 은 여기서 그것으로 접는다. */
const UNSET = "";

/** 설정의 왼쪽 메뉴(공통 ④). 「사용자」 · 「러너」는 **관리자에게만 그린다** — 그 문(`/admin/users`)이
 *  관리자 전용이라 일반 사용자에게 그리면 403 만 보는 칸이 된다. */
export function SettingsPage() {
  const auth = useMe();
  const tab = useRouteParam("tab");
  const isAdmin = auth?.me.role === "admin";
  const showUsers = tab === "users" && isAdmin;
  const showAuth = tab === "auth" && isAdmin;
  const showRunners = tab === "runners" && isAdmin;
  const showMcp = tab === "mcp" && isAdmin;
  /* 레일은 하위 보기가 둘 이상일 때만 선다(A안) — 관리자가 아니면 「배차 벤더」 하나뿐이라 레일이 없다. */
  const side = isAdmin ? (
    <>
      <SideGroup>{tx("nav.settings")}</SideGroup>
      <SideLink icon="bot" href="#/settings" active={!showUsers && !showAuth && !showRunners && !showMcp}>
        {tx("settings.vendors.title")}
      </SideLink>
      <SideLink icon="users" href="#/settings?tab=users" active={showUsers}>
        {tx("users.title")}
      </SideLink>
      <SideLink icon="shieldCheck" href="#/settings?tab=auth" active={showAuth}>
        {tx("policy.title")}
      </SideLink>
      <SideLink icon="server" href="#/settings?tab=runners" active={showRunners}>
        {tx("runners.title")}
      </SideLink>
      <SideLink icon="plug" href="#/settings?tab=mcp" active={showMcp}>
        {tx("mcpTokens.title")}
      </SideLink>
    </>
  ) : undefined;
  if (showMcp) {
    return (
      <AppShell route="/settings" sidebarTitle={tx("settings.menu")} sidebar={side}>
        <PageHead title={tx("nav.settings")} />
        <McpTokensSection />
      </AppShell>
    );
  }
  if (showRunners) {
    return (
      <AppShell route="/settings" sidebarTitle={tx("settings.menu")} sidebar={side}>
        <PageHead title={tx("nav.settings")} />
        <RunnersSection />
      </AppShell>
    );
  }
  if (showAuth) {
    return (
      <AppShell route="/settings" sidebarTitle={tx("settings.menu")} sidebar={side}>
        <PageHead title={tx("nav.settings")} />
        <AuthPolicySection />
      </AppShell>
    );
  }
  if (showUsers) {
    return (
      <AppShell route="/settings" sidebarTitle={tx("settings.menu")} sidebar={side}>
        <PageHead title={tx("nav.settings")} />
        <UsersSection meId={auth?.me.id} />
      </AppShell>
    );
  }
  return <VendorsSettings side={side} canEdit={isAdmin || !auth} />;
}

/** `canEdit` 가 거짓이면 읽기만 한다 — 저장 문(`PUT /admin/dispatch/vendors`)이 관리자 전용이라,
 *  고를 수 있게 그려 두면 누른 뒤에야 403 을 본다. */
function VendorsSettings({ side, canEdit }: { side: ReactNode; canEdit: boolean }) {
  const [saved, setSaved] = useState<Saved | null>(null);
  const [picks, setPicks] = useState<Picks>({});
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/dispatch/vendors`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((s: Saved) => {
        setSaved(s);
        setPicks(s.tracks);
      })
      .catch((e: Error) => setLoadErr(e.message));
  }, []);

  const changed = saved ? PICKABLE.filter((t) => (picks[t.key] ?? null) !== (saved.tracks[t.key] ?? null)) : [];
  const dirty = changed.length > 0;

  async function save() {
    setSaving(true);
    setSaveErr(null);
    try {
      const r = await fetch(`${API}/admin/dispatch/vendors`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        // **전체 문서다** — 네 트랙을 늘 다 보낸다. 안 보낸 트랙은 서버가 「지웠다」로
        // 본다(`connection.rs` 의 `tiers` 와 같은 규칙).
        body: JSON.stringify({ tracks: picks }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) throw new Error(data?.message ?? `HTTP ${r.status}`);
      const s: Saved = data;
      setSaved(s);
      setPicks(s.tracks);
    } catch (e) {
      setSaveErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell route="/settings" sidebarTitle={tx("settings.menu")} sidebar={side}>
      <PageHead title={tx("nav.settings")} />
      <h2>{tx("settings.vendors.title")}</h2>
      <p className="muted">
        {rich(tx("settings.vendors.lead", hole("plugged")), { plugged: <b>{tx("settings.vendors.plugged")}</b> })}
      </p>
      <p className="muted">
        {rich(tx("settings.vendors.envNote", hole("env")), { env: <b>.env</b> })}
      </p>

      {!canEdit ? <Alert tone="info">{tx("settings.vendors.readOnly")}</Alert> : null}
      {loadErr ? <Alert tone="error">{tx("settings.vendors.loadFailed", { err: loadErr })}</Alert> : null}

      {saved ? (
        <div className="vendors__table" role="table" aria-label={tx("settings.vendors.table")}>
          <div className="vendors__row vendors__row--head" role="row">
            <span className="vendors__track" role="columnheader">
              {tx("settings.vendors.track")}
            </span>
            <span role="columnheader">{tx("settings.vendors.vendor")}</span>
          </div>
          {PICKABLE.map((t) => (
            <div className="vendors__row" role="row" key={t.key}>
              <span className="vendors__track" role="rowheader">
                {t.label}
              </span>
              <Select
                size="md"
                disabled={!canEdit}
                value={picks[t.key] ?? UNSET}
                onChange={(e) => setPicks((p) => ({ ...p, [t.key]: e.target.value || null }))}
              >
                <option value={UNSET}>{tx("settings.vendors.unset")}</option>
                {saved.knownVendors.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </div>
      ) : !loadErr ? (
        <Loading text={tx("settings.vendors.loading")} />
      ) : null}

      <p className="muted vendors__note">
        {rich(tx("settings.vendors.noFallback", hole("no")), { no: <b>{tx("settings.vendors.noFallbackBold")}</b> })}
      </p>

      {saveErr ? <Alert tone="error">{tx("settings.vendors.saveFailed", { err: saveErr })}</Alert> : null}

      {dirty ? (
        <div className="vendors__save">
          <span>
            {tx("settings.vendors.unsaved")}
            {changed.map((t) => (
              <Chip key={t.key} tone="warning">
                {tx("settings.vendors.changed", { track: t.label })}
              </Chip>
            ))}
          </span>
          <span className="vendors__savebtns">
            <Button onClick={() => saved && setPicks(saved.tracks)}>{tx("settings.vendors.revert")}</Button>
            <Button variant="primary" onClick={save} loading={saving}>
              {tx("common.action.save")}
            </Button>
          </span>
        </div>
      ) : null}
    </AppShell>
  );
}
